import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { usePathname } from 'expo-router';
import { useAppTheme } from '@/theme';
import { FrameContext, MAX_CONTENT_WIDTH } from '@/theme/responsive';
import { RAIL_WIDTH, SideRail, useSideRail, useSideRailRoute } from '@/components/navigation/SideRail';

const BLEED = new Set(['/welcome', '/login', '/register', '/forgot-password', '/reset-password', '/otp-verification', '/location-permission']);

/**
 * Holds the whole app. On a phone it is just a full-screen view. On a wide browser window (a laptop
 * or desktop monitor) the navigation moves from the bottom bar to a side rail pinned to the left, and
 * the content fills the space that is left, up to MAX_CONTENT_WIDTH. Each page then narrows itself
 * with <PageWidth> where that reads better, so a form is never stretched across a monitor.
 *
 * The tree is the same in every case and only the style changes, so resizing the window, or the rail
 * appearing after sign-in, never remounts the screens underneath (which would throw away the user's
 * place in the app).
 */
export function ResponsiveFrame({ children }: { children: React.ReactNode }) {
  const { colors } = useAppTheme();
  const rail = useSideRail();
  const pathname = usePathname();
  // The welcome picture and the sign-in split screens run edge to edge.
  const bleed = BLEED.has(pathname);
  const railWidth = rail ? RAIL_WIDTH : 0;
  // The server cannot know the window width, so the first paint has no rail and the content would jump
  // 248px to the right once the browser measured it. Until then, a spacer holds the rail's place on wide
  // windows (customer.css shows it only from 1024px up); it is gone as soon as the real rail is drawn.
  const reserveRail = useSideRailRoute() && !rail;
  const frame = useMemo(() => ({ railWidth, pageMax: Infinity }), [railWidth]);

  return (
    <FrameContext.Provider value={frame}>
      <View style={[styles.outer, { backgroundColor: colors.paper }]}>
        {rail ? <SideRail /> : null}
        {reserveRail ? <View dataSet={{ railSpacer: '' }} style={{ width: RAIL_WIDTH }} /> : null}
        <View style={styles.area}>
          <View style={[styles.column, !bleed && styles.capped]}>{children}</View>
        </View>
      </View>
    </FrameContext.Provider>
  );
}

const styles = StyleSheet.create({
  outer: { flex: 1, flexDirection: 'row' },
  area: { flex: 1, alignItems: 'center' },
  column: { flex: 1, width: '100%' },
  capped: { maxWidth: MAX_CONTENT_WIDTH },
});
